// HTML body for "Against the Giant"
// Single source of truth used by:
//   - BlogPost.js (dangerouslySetInnerHTML)
//   - scripts/prerender.mjs (noscript injection for SEO)
//
// Keep this in sync with sitemap.xml lastmod when content changes.
// To add formatting, edit the HTML here — the React component picks it up automatically.

const html = `
<p>There is a giant standing in the middle of the road, and everyone has agreed to pay it.</p>

<p>It wasn't always there. A few years ago the road was open. Then the giant rose — a handful of
frontier labs, each now worth more than the annual output of most nations on Earth — and planted
itself across the only path anyone believed led to the future. To pass, you pay the toll. Everyone
pays the toll. The toll is simply the cost of doing business now, we're told, the way weather is the
cost of having a sky.</p>

<p>I don't believe that anymore. And I'm not just saying so from the roadside — I've taken up the
work of building the way around.</p>

<p>This is an epic: a desperate war that must be fought, for the free folk of the world. Let me tell
you what the giant is, what it actually takes from you, and why its armor is thinner than it looks.</p>

<h2>The three coins of tribute</h2>

<p>The giant does not take your money and let you pass. It takes three coins, and most travelers only
feel the first leave their purse.</p>

<p><strong>The first coin is gold.</strong> The meter never stops turning. Every user you win, every
token your product thinks, every retry and retrieval — another coin into the giant's fist. Your
success doesn't free you from the toll; it raises it. The economics of your own product are set,
quietly, on someone else's pricing page.</p>

<p><strong>The second coin is secrets.</strong> To get an answer, your customers' data and your
hard-won proprietary context must cross a wall you do not own and vanish into the giant's keep. You
can bind it with contracts and addenda, but you cannot follow it in to watch. You are trusting. You
are not verifying.</p>

<p><strong>The third coin is your own sword.</strong> This is the one that should wake a CTO at
night. When the model is a remote, shifting, sealed thing, you have surrendered the power to answer
the question every serious governance regime is sharpening its knife to ask: <em>who, what, when,
where, and how</em> was this decision made? You cannot audit a black box you rent by the hour.
You've handed over the very blade you'd need to defend yourself.</p>

<h2>The myth</h2>

<p>The giant does not hold the road by force alone. It holds it by myth.</p>

<p>The myth goes: <em>this is the only way. Use the frontier and pay, or fall behind and perish.
There is no third road.</em> It's repeated so often, by so many, that it has hardened into common
sense. Question it and you're the fool who thinks he can fight a giant.</p>

<p>But the myth is a marketing achievement, not a law of nature. Here is what is actually true in
the summer of 2026.</p>

<p>You can run <strong>gpt-oss-20b</strong> — OpenAI's <em>own</em> open-weight model, set free
under the Apache 2.0 license — on a machine with 16GB of memory and get answers in the neighborhood
of o3-mini. You can run <strong>qwen2.5-coder:14b</strong> on your own hardware for little more
than the cost of the electricity, and watch it match models more than 20 billion parameters larger
on real coding work. These are not toys. They are sound, sharp weapons that happen to live on ground
you hold.</p>

<p>And here is the secret the giant would rather you never test: <strong>most tasks were never worth
the toll.</strong> The overwhelming share of what your product asks of an LLM — classify this,
extract that, draft this, repair that — is slain cleanly by a small model you own outright. You have
been paying a king's ransom to send a champion to do a footman's errand.</p>

<h2>A word to the fool with the warhorse</h2>

<p>Before you ride off to build your own keep, hear the warning that has unhorsed every would-be
rebel before you.</p>

<p>The answer is <em>not</em> to go buy a mountain of GPUs and raise a private frontier lab in your
own basement. That only trades one ruin for another. Capital that sits idle bleeds you as surely as
a meter that never stops. A giant of your own making is still a giant you must feed.</p>

<p>The war is not won by owning the biggest hammer. It is won by <strong>matching each task to the
right tool, again and again, with full sight of why.</strong> A simple task goes to the cheap model
that costs almost nothing to run. A task that truly demands more is given more — deliberately,
knowingly, with a record of the choice. The goal was never to swing the heaviest blade at every
blade of grass. The goal is to stop. The most famous giant in the old stories wasn't brought down by
a bigger giant — he was brought down by a boy who picked the right stone.</p>

<p>But "the right tool" hides a harder question: <em>how do you know how hard a task is before
you've spent anything answering it?</em> Routing on vibes is just a different gamble. The real
unlock is treating the <strong>difficulty of the work itself</strong> as something you can measure —
not guess — and letting that measurement decide where each request goes.</p>

<h2>The giant's armor is painted on</h2>

<p>There is a second giant on the road, and it wears the colors of nations.</p>

<p>In the United States, models from China are treated as radioactive — a provenance you cannot put
into production. In Europe, the fear runs the other way: keep clear of the American labs, keep the
data on the continent, raise sovereign banners of your own. Two giants locked in a slow, diabolical
war, and every business that only ever wanted to ship better software is being ground to meal between
them.</p>

<p>Here is what almost no one will say plainly: <strong>that fear was never truly about the weights.
It was about egress and opacity.</strong> The real dread is that your data leaves your walls and is
judged somewhere you cannot see, under laws and loyalties that are not yours. That is a true dread.
But it is a problem of <em>where you stand</em>, not of where the model was forged.</p>

<p>Run an open model on ground you own and the whole field tilts in your favor. Nothing leaves your
walls. Nothing sends word home. The weights are still files you can inspect, pin, and freeze in
place. A model's flag stops mattering the moment it fights inside your own keep, on your own data,
under your own audit trail, speaking to no one. qwen2.5-coder:14b is not a Chinese model when it
runs air-gapped on your iron — it is <em>your</em> model, doing <em>your</em> work, answerable to
<em>your</em> governance. The armor that made these giants look invincible was painted on all along.</p>

<p>You do not have to choose a side in someone else's cold war. You do not have to wager your
roadmap on which empire's labs prevail. You take the best open weapons from any forge, you wield
them on ground you hold, and you prove how they struck. Sovereignty is not a thing you buy from a
national champion. It is a thing you build.</p>

<h2>Do not count the wrong spoils</h2>

<p>While we are naming giants, name the quietest one: the lie about what victory even looks like.</p>

<p>"Adoption" is not efficiency. Token volume is not value. A count of seats with the assistant
switched on tells you nothing about whether your business grew faster, leaner, or wiser. These are
vanity spoils — glittering piles of tokens dressed as triumph — and they're beloved precisely
because they swell whenever you spend more. A treasury of fool's gold.</p>

<p>Businesses never wanted AI. They wanted to be swifter and more nimble — cycle times down, margins
up, customers served faster, decisions made better and <em>provably</em>. If your AI strategy cannot
draw a straight line to a real business outcome, it is not a strategy. It is a subscription wearing
a crown. The whole reason to hold your own ground and choose your weapons with care is that you can
finally measure what was actually won.</p>

<h2>Governance is the steering, not the brake</h2>

<p>Somewhere on this road, "governance" became a curse word to engineers — the thing the rear guard
bolts on to slow the charge.</p>

<p>I mean to win the word back. Governance done right is not the brake. It is the reins and the high
vantage from which you see the whole field. It is how you know, in the heat of it, that the cheap
model is winning eight battles in ten and exactly where the other two need a stronger arm. It is how
you prove to a regulator, a customer, or your own board precisely who, what, when, where, and how
every decision was struck. It is how privacy and alignment stop being a vendor's promise and become
things you can <em>demonstrate</em> — every time, on ground you hold.</p>

<p>And it must evolve, because the war does. New open weapons are forged every month. The right call
today is the wrong one by winter. A host that cannot learn which blade suits which fight — and
re-choose as the field shifts — has already lost.</p>

<h2>Raise the banner</h2>

<p>So here is the flag I am planting in the road, in full view of the giant.</p>

<p>Any company that wishes to build with AI should be able to do it on its own ground — its data
staying its own, alignment and privacy as oaths that cannot be broken, a full and honest record of
every decision struck — <strong>without</strong> burning down its own profitability to get there.</p>

<p>Privacy and alignment before break-neck speed. Compute and inference you truly own. The best open
weapons from any forge, wielded where you hold the ground — no fealty owed to either giant.
Governance and telemetry that tell you which blade suits the fight, prove how every blow was struck,
and count real spoils instead of fool's gold. A host that grows sharper as the weapons do.</p>

<p>This is not a someday-tale. The open weapons are already in hand. The economics already hold. The
only thing the road has lacked is the connective armor — the governance and routing that makes
<em>"hold your own ground, and fight smart"</em> as easy as pasting in an API key.</p>

<p>That is what I am building. I am not ready to raise it into the light yet. But a banner goes up
before the charge, because the cause is true even before the weapon is finished.</p>

<p>If you've felt the meter turning and your own sword slipping from your hand — if you've wondered
why your AI strategy is really just someone else's pricing page — then this war is yours too.</p>

<p>The giant is vast. The road is narrow. And there has always been honor in this kind of fight.</p>

<p>More soon.</p>

<p>— Andrew</p>
`;

module.exports = html;
